const SESSION_STORAGE_KEY = 'annajm_browser_session_id';
const TRACK_COOLDOWN_MS = 10_000;
const recentTrackByPage = new Map<string, number>();

function shouldSkipDuplicateTrack(page: string): boolean {
  if (typeof window === 'undefined') return false;

  const storageKey = `annajm_track_visit:${page}`;
  const now = Date.now();
  const stored = Number(sessionStorage.getItem(storageKey) || 0);
  const last = Math.max(stored, recentTrackByPage.get(page) ?? 0);

  if (now - last < TRACK_COOLDOWN_MS) return true;

  recentTrackByPage.set(page, now);
  sessionStorage.setItem(storageKey, String(now));
  return false;
}

function getOrCreateBrowserSessionId(): string {
  if (typeof window === 'undefined') return '';

  const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;

  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  return id;
}

export function detectPlatform(): string {
  if (typeof navigator === 'undefined') return 'unknown';

  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macos';
  if (/Linux/i.test(ua)) return 'linux';

  return navigator.platform || 'unknown';
}

type TrackUserSessionInput = {
  accessToken: string;
  page?: string;
};

export async function trackUserSession({
  accessToken,
  page = '/app',
}: TrackUserSessionInput): Promise<void> {
  const baseUrl = (process.env.NEXT_PUBLIC_BASE_URL || '').replace(/\/$/, '');
  if (!baseUrl || !accessToken) return;
  if (shouldSkipDuplicateTrack(page)) return;

  try {
    await fetch(`${baseUrl}/api/analytics/track-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: accessToken,
        platform: detectPlatform(),
        page,
        sessionId: getOrCreateBrowserSessionId(),
        userAgent: navigator.userAgent,
      }),
    });
  } catch {
    // Non-blocking analytics
  }
}
